import { Box, Flex } from 'components'
import { useContext, useState } from 'react'
import { ITabsLayout } from 'types'
import Layout from '..'
import { LayoutHelpers } from '../../../desktopTypes'
import { addLayoutTag } from '../../../utils/helpers'
import { parsePadding, useLayoutItems, useParseText } from '../../../utils/layoutUtils'
import { ExplorerConfigContext } from '../../providers/ExplorerConfigProvider'
import { SDivider, SText, SView } from '../../scaled'
import EmptyLayout from '../EmptyLayout'

export default function TabsLayout({
  playVideo,
  layouts,
  orientation,
  keyPath,
  style,
  title,
}: Omit<ITabsLayout, 'type'> & LayoutHelpers) {
  const parseText = useParseText()
  const { theme } = useContext(ExplorerConfigContext)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const items = useLayoutItems(
    layouts.map(l => ({ ...l, style: { grow: 1 } })),
    keyPath
  )

  if (items.length === 0) return <EmptyLayout type="tabs" />

  const itemTitles = items.map(i => i.tabTitle || i.title?.text)

  async function setSelected(index: number) {
    setSelectedIndex(index)
  }

  const padding = parsePadding({ style, title })
  const isHorizontal = orientation === 'horizontal'

  return (
    <SView
      {...padding}
      grow={1}
      gap={20}
      direction={isHorizontal ? 'column' : 'row'}
      {...addLayoutTag('tabs')}
    >
      <SView {...(isHorizontal ? { direction: 'row', h: 100 } : { direction: 'column', w: 180 })}>
        {itemTitles.map((title, i) => (
          <SView
            key={i}
            cursor="pointer"
            onClick={() => setSelected(i)}
            direction={isHorizontal ? 'column' : 'row'}
            align="stretch"
            gap={10}
            justify="space-between"
          >
            <SText
              color={i === selectedIndex ? theme.color.primary : 'white'}
              style={{ flexShrink: 1 }}
              py={10}
            >
              {parseText(title)}
            </SText>
            <SView direction={isHorizontal ? 'column' : 'row'}>
              <SDivider vertical={!isHorizontal} />
              <Box
                opacity={i === selectedIndex ? 1 : 0}
                transition="opacity"
                transitionDuration="fast"
              >
                <Flex flex={1} justifyContent="center">
                  <SView
                    background={theme.color.primary}
                    borderRadius={5}
                    {...(isHorizontal ? { h: 3, w: 100 } : { h: 50, w: 3 })}
                  />
                </Flex>
              </Box>
            </SView>
          </SView>
        ))}
        <SView direction={isHorizontal ? 'column' : 'row'} justify="space-between" flex={1}>
          <SView />
          <SView direction={isHorizontal ? 'column' : 'row'}>
            <SDivider vertical={!isHorizontal} />
            <SView {...(isHorizontal ? { h: 3, w: 100 } : { h: 50, w: 3 })} />
          </SView>
        </SView>
      </SView>

      <SView flex={1}>
        {items.map(({ key, ...rest }, i) => (
          <Box
            key={i}
            opacity={i === selectedIndex ? 1 : 0}
            transition="opacity"
            transitionDuration="fast"
          >
            <Flex
              flex={1}
              justifyContent="center"
              position="absolute"
              inset={0}
              zIndex={i === selectedIndex ? 1 : 0}
            >
              <Layout playVideo={playVideo} {...rest} />
            </Flex>
          </Box>
        ))}
      </SView>
    </SView>
  )
}
